/* product-page.js — runtime for sections/main-product.liquid.
   Uses the global.js contracts (fetchConfig, publish/subscribe, PUB_SUB_EVENTS, ModalDialog,
   trapFocus, pauseAllMedia, debounce). Every custom element is guarded with customElements.get
   so a second definition elsewhere never throws. */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ slider-component */
  if (!customElements.get('slider-component')) {
    class SliderComponent extends HTMLElement {
      constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.hasDots = false;
        this.scrollMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;
        this.verticalDesktop = this.dataset.desktopVertical === 'true';
        this.verticalMobile = this.dataset.mobileVertical === 'true';
        this.vertical = false;
        if (!this.slider || !this.nextButton) return;

        this.slider.addEventListener('scroll', this.update.bind(this));
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

        this.sliderControlWrapper = this.querySelector('.slider-buttons');
        if (this.sliderControlWrapper && this.sliderControlWrapper.querySelector('.slider-counter__link')) {
          this.sliderFirstItemNode = this.slider.querySelector('.slider__slide');
          this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
          this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
          this.hasDots = true;
        }
        this.initPages();
        const resizeObserver = new ResizeObserver(() => this.initPages());
        resizeObserver.observe(this.slider);
      }

      linkToSlide(event) {
        event.preventDefault();
        const index = this.sliderControlLinksArray.indexOf(event.currentTarget);
        let hiddenBefore = 0;
        for (let i = 0; i < index; i++) {
          if (this.sliderControlLinksArray[i].classList.contains('hidden')) hiddenBefore++;
        }
        const visibleIndex = index - hiddenBefore;
        const slideDelta = visibleIndex + 1 - this.currentPage;
        const current = this.vertical ? this.slider.scrollTop : this.slider.scrollLeft * this.scrollMultiplier;
        const size = this.vertical ? this.sliderFirstItemNode.clientHeight : this.sliderFirstItemNode.clientWidth;
        const target = current + size * slideDelta;
        if (this.vertical) this.slider.scrollTo({ top: target, behavior: 'smooth' });
        else this.slider.scrollTo({ left: target * this.scrollMultiplier, behavior: 'smooth' });
      }

      initPages() {
        this.vertical = window.innerWidth < 750 ? this.verticalMobile : this.verticalDesktop;
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((el) => el.clientWidth > 0);
        if (this.sliderItemsToShow.length < 2) return;
        if (this.vertical) {
          const first = this.sliderItemsToShow[0].offsetTop;
          this.sliderItemOffset = this.sliderItemsToShow[1].offsetTop - first;
          this.slidesPerPage = Math.floor((this.slider.clientHeight - first) / this.sliderItemOffset);
        } else {
          const first = this.sliderItemsToShow[0].offsetLeft * this.scrollMultiplier;
          this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft * this.scrollMultiplier - first;
          this.slidesPerPage = Math.floor((this.slider.clientWidth - first) / this.sliderItemOffset);
        }
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        this.update();
      }

      resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
      }

      update() {
        if (!this.slider || !this.nextButton) return;
        const previousPage = this.currentPage;
        this.currentPage = this.vertical
          ? Math.round(this.slider.scrollTop / this.sliderItemOffset) + 1
          : Math.round((this.slider.scrollLeft * this.scrollMultiplier) / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = this.currentPage;
          this.pageTotalElement.textContent = this.totalPages;
        }
        if (this.currentPage !== previousPage) {
          this.dispatchEvent(
            new CustomEvent('slideChanged', {
              detail: { currentPage: this.currentPage, currentElement: this.sliderItemsToShow[this.currentPage - 1] },
            })
          );
        }
        if (this.hasDots) {
          let visible = 0;
          const active = this.currentPage - 1;
          this.sliderControlLinksArray.forEach((link) => {
            link.classList.remove('slider-counter__link--active');
            link.removeAttribute('aria-current');
            if (!link.classList.contains('hidden')) {
              if (visible === active) {
                link.classList.add('slider-counter__link--active');
                link.setAttribute('aria-current', 'true');
              }
              visible++;
            }
          });
        }
        if (this.enableSliderLooping) return;
        const first = this.sliderItemsToShow[0];
        const last = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
        if (this.vertical) {
          if (this.slider.scrollTop === 0) this.prevButton.setAttribute('disabled', 'disabled');
          else this.prevButton.removeAttribute('disabled');
        } else if (this.isSlideVisible(first) && this.slider.scrollLeft === 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
        if (this.isSlideVisible(last, true)) this.nextButton.setAttribute('disabled', 'disabled');
        else this.nextButton.removeAttribute('disabled');
      }

      isSlideVisible(element, tolerant = false, offset = 0, checkStart = false) {
        const tolerance = tolerant ? 10 : 0;
        if (this.vertical) {
          const end = element.offsetTop + element.clientHeight;
          const start = element.offsetTop;
          const viewStart = this.slider.scrollTop - tolerance + offset;
          const viewEnd = this.slider.scrollTop + this.slider.clientHeight + tolerance - offset;
          return end <= viewEnd && (!checkStart || start >= viewStart);
        }
        const end = element.offsetLeft * this.scrollMultiplier + element.clientWidth;
        const start = element.offsetLeft * this.scrollMultiplier;
        const viewStart = this.slider.scrollLeft * this.scrollMultiplier - tolerance + offset;
        const viewEnd = this.slider.scrollLeft * this.scrollMultiplier + this.slider.clientWidth + tolerance - offset;
        return end <= viewEnd && (!checkStart || start >= viewStart);
      }

      onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;
        if (this.vertical) {
          const current = this.slider.scrollTop;
          const target = event.currentTarget.name === 'next' ? current + step * this.sliderItemOffset : current - step * this.sliderItemOffset;
          this.slider.scrollTo({ top: target, behavior: 'smooth' });
        } else {
          const current = this.slider.scrollLeft * this.scrollMultiplier;
          this.slideScrollPosition =
            event.currentTarget.name === 'next' ? current + step * this.sliderItemOffset : current - step * this.sliderItemOffset;
          this.slider.scrollTo({ left: this.slideScrollPosition * this.scrollMultiplier });
        }
      }
    }
    customElements.define('slider-component', SliderComponent);
  }

  /* ------------------------------------------------------------------ media-gallery */
  if (!customElements.get('media-gallery')) {
    customElements.define(
      'media-gallery',
      class extends HTMLElement {
        constructor() {
          super();
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]'),
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          };
          this.mql = window.matchMedia('(min-width: 750px)');
          this.prependMedia = this.dataset.disablePrepend !== 'true';
          if (!this.elements.thumbnails) return;
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        onSlideChanged(event) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${event.detail.currentElement.dataset.mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        setActiveMedia(mediaId, prepend) {
          const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
          if (!activeMedia) return;
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('is-active'));
          activeMedia.classList.add('is-active');
          if (prepend && this.prependMedia) {
            activeMedia.parentElement.prepend(activeMedia);
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (this.elements.thumbnails) activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') activeMedia.scrollIntoView({ behavior: 'smooth' });
          });
          this.playActiveMedia(activeMedia);
          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        setActiveThumbnail(thumbnail) {
          if (!this.elements.thumbnails || !thumbnail) return;
          this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
          thumbnail.querySelector('button').setAttribute('aria-current', true);
          if (this.elements.thumbnails.isSlideVisible && !this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) {
            this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
          }
        }

        announceLiveRegion(activeItem, position) {
          const image = activeItem.querySelector('.product__modal-opener--image img');
          if (!image) return;
          image.onload = () => {
            this.elements.liveRegion.setAttribute('aria-hidden', false);
            this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
            setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
          };
          image.src = image.src;
        }

        playActiveMedia(activeItem) {
          window.pauseAllMedia();
          const deferredMedia = activeItem.querySelector('.deferred-media');
          if (deferredMedia) deferredMedia.loadContent(false);
        }

        preventStickyHeader() {
          this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
          if (this.stickyHeader) this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
        }

        removeListSemantic() {
          if (!this.elements.viewer.slider) return;
          this.elements.viewer.slider.setAttribute('role', 'presentation');
          this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
        }
      }
    );
  }

  /* ------------------------------------------------------------------ product-modal (lightbox) */
  if (!customElements.get('product-modal')) {
    customElements.define(
      'product-modal',
      class extends ModalDialog {
        constructor() {
          super();
        }
        hide() {
          super.hide();
        }
        show(opener) {
          super.show(opener);
          this.showActiveMedia();
        }
        showActiveMedia() {
          const id = this.openedBy.getAttribute('data-media-id');
          this.querySelectorAll(`[data-media-id]:not([data-media-id="${id}"])`).forEach((element) => element.classList.remove('active'));
          const activeMedia = this.querySelector(`[data-media-id="${id}"]`);
          if (!activeMedia) return;
          activeMedia.classList.add('active');
          activeMedia.scrollIntoView();
          const container = this.querySelector('[role="document"]');
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }
      }
    );
  }

  /* ------------------------------------------------------------------ splide-component */
  if (!customElements.get('splide-component')) {
    class SplideComponent extends HTMLElement {
      constructor() {
        super();
        this.sliderContainer = this.querySelector('.splide');
        document.addEventListener('shopify:section:load', () => this.initSlider());
      }
      connectedCallback() {
        this.initSlider();
      }
      initSlider() {
        if (!this.sliderContainer || this.splide) return;
        if (!window.Splide) {
          if (!this._retry) this._retry = setInterval(() => window.Splide && (clearInterval(this._retry), this.initSlider()), 100);
          return;
        }
        const d = this.dataset;
        const int = (v, fallback) => (v && !isNaN(parseInt(v)) ? parseInt(v) : fallback);
        this.type = d.type || 'slide';
        this.typeMobile = d.typeMobile || this.type;
        this.direction = d.direction || (document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr');
        this.rewind = this.type === 'fade';
        this.autoplay = d.autoplay === 'true';
        this.autoplaySpeed = int(d.autoplaySpeed, 5) * 1000;
        this.drag = d.drag !== 'false' && (d.drag !== 'free' || 'free');
        this.focus = d.focus || 0;
        this.trimSpace = d.trimSpace !== 'false';
        this.arrows = d.arrows !== 'false';
        this.arrowsColor = d.arrowsColor ? ` color-${d.arrowsColor}` : '';
        this.pagination = d.pagination !== 'false';
        this.omitEnd = d.omitEnd !== 'false';
        this.dotsColor = d.dotsColor ? ` color-${d.dotsColor} dots-custom-color` : '';
        this.slidesDesktop = int(d.slidesDesktop, 3);
        this.autoWidth = d.autoWidth === 'true';
        this.slidesMobile = int(d.slidesMobile, 1);
        this.perMoveDesktop = int(d.perMoveDesktop, 1);
        this.perMoveMobile = int(d.perMoveMobile, 1);
        this.gapDesktop = d.gapDesktop ? parseInt(d.gapDesktop) : 30;
        this.gapMobile = d.gapMobile ? parseInt(d.gapMobile) : 15;
        this.paddingCalcDesktop = d.paddingCalcDesktop === 'true';
        this.rightPaddingDesktop = parseInt(d.sidePaddingDesktop) || 0;
        this.leftPaddingDesktop = d.paddingCalcDesktop ? 0 : this.rightPaddingDesktop;
        this.paddingCalcMobile = d.paddingCalcMobile === 'true';
        this.rightPaddingMobile = parseInt(d.sidePaddingMobile) || 0;
        this.leftPaddingMobile = this.paddingCalcMobile ? 0 : this.rightPaddingMobile;
        this.destroyDesktop = d.destroyDesktop === 'true';
        this.destroyMobile = d.destroyMobile === 'true';
        this.config = {
          type: this.type,
          direction: this.direction,
          rewind: this.rewind,
          autoplay: this.autoplay,
          interval: this.autoplaySpeed,
          drag: this.drag,
          focus: this.focus,
          trimSpace: this.trimSpace,
          arrows: this.arrows,
          pagination: this.pagination,
          omitEnd: this.omitEnd,
          perPage: this.slidesDesktop,
          perMove: this.perMoveDesktop,
          autoWidth: this.autoWidth,
          gap: this.gapDesktop,
          padding: { left: this.leftPaddingDesktop, right: this.rightPaddingDesktop },
          destroy: this.destroyDesktop,
          classes: { arrow: `splide__arrow${this.arrowsColor}`, page: `splide__pagination__page${this.dotsColor}` },
          easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
          breakpoints: {
            749: {
              omitEnd: this.omitEnd,
              type: this.typeMobile,
              perPage: this.slidesMobile,
              perMove: this.perMoveMobile,
              gap: this.gapMobile,
              padding: { left: this.leftPaddingMobile, right: this.rightPaddingMobile },
              destroy: this.destroyMobile,
            },
          },
        };
        const splide = new Splide(this.sliderContainer, this.config);
        this.splide = splide;
        const activate = (slide) => {
          if (!slide) return;
          slide.classList.add('is-instant-active');
          this.setActiveSlideHeight(slide);
        };
        splide.on('mounted', () => {
          const slide = splide.Components.Elements.slides[splide.index];
          if (slide) {
            const img = slide.querySelector('img');
            if (img && !img.complete) img.addEventListener('load', () => activate(slide));
            else activate(slide);
          }
          window.addEventListener('resize', debounce(() => this.setActiveSlideHeight(splide.Components.Elements.slides[splide.index]), 200));
        });
        splide.on('move', () => {
          const slides = splide.Components.Elements.slides;
          if (!slides) return;
          const next = slides[splide.index];
          if (next) {
            slides.forEach((s) => s.classList.remove('is-instant-active'));
            activate(next);
          }
        });
        if (d.pauseVideos === 'true') {
          splide.on('hidden', (slideObj) => {
            const video = slideObj.slide.querySelector('internal-video');
            if (!video || !video.video) return;
            const action = video.dataset.actionOnInactive;
            if (action === 'pause') {
              video.video.pause();
              video.classList.remove('internal-video--playing');
            } else if (action === 'mute') {
              video.video.muted = true;
              video.classList.add('internal-video--muted');
            }
          });
        }
        splide.mount();
      }
      setActiveSlideHeight(slide) {
        if (slide) this.style.setProperty('--active-slide-height', `${slide.offsetHeight}px`);
      }
    }
    customElements.define('splide-component', SplideComponent);
  }

  /* ------------------------------------------------------------------ helpers */
  function getSectionsToRender() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((s) => s.id || s.section);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  /* ------------------------------------------------------------------ product-form */
  if (!customElements.get('product-form')) {
    customElements.define(
      'product-form',
      class extends HTMLElement {
        constructor() {
          super();
          this.form = this.querySelector('form');
          if (!this.form) return;
          this.variantIdInput.disabled = false;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          this.submitButton = this.querySelector('[type="submit"]');
          this.submitButtonText = this.submitButton ? this.submitButton.querySelector('.main-atc__label__text, span') : null;
          this.variantsContainer = this.querySelector('.product-form__variants');
          if (document.querySelector('cart-drawer') && this.submitButton) this.submitButton.setAttribute('aria-haspopup', 'dialog');
          this.hideErrors = this.dataset.hideErrors === 'true';
        }

        get variantIdInput() {
          return this.form.querySelector('[name=id]');
        }

        onSubmitHandler(evt) {
          evt.preventDefault();
          this.handleSubmit(evt);
        }

        collectItems() {
          const items = [];
          if (this.dataset.main === 'true') {
            document.querySelectorAll('product-info-upsell[data-selected="true"]').forEach((upsell) => {
              const id = parseInt(upsell.dataset.id);
              if (id) items.push({ id: id, quantity: 1 });
            });
          }
          const qtyInput = this.form.querySelector('[name=quantity]');
          const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
          items.push({ id: parseInt(this.variantIdInput.value), quantity: quantity });
          return items;
        }

        handleSubmit(evt) {
          if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;
          this.handleErrorMessage();
          const loadingButtons = [this.submitButton, ...document.querySelectorAll(`#SectionAtcBtn-${this.dataset.section}`)].filter(Boolean);
          loadingButtons.forEach((btn) => {
            btn.setAttribute('aria-disabled', true);
            btn.classList.add('loading');
            const spinner = btn.querySelector('.loading-overlay__spinner');
            if (spinner) spinner.classList.remove('hidden');
          });

          const items = this.collectItems();
          const body = {
            items: items,
            sections: getSectionsToRender(),
            sections_url: window.location.pathname,
          };
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(body);

          fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: this.variantIdInput.value,
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);
                if (this.submitButton) this.submitButton.setAttribute('aria-disabled', true);
                this.error = true;
                return;
              }
              if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }
              if (!this.error) {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: this.variantIdInput.value,
                  cartData: response,
                });
              }
              this.error = false;
              if (typeof this.cart.renderContents === 'function') this.cart.renderContents(response);
              if (typeof this.cart.open === 'function') this.cart.open();
            })
            .catch((e) => console.error(e))
            .finally(() => {
              loadingButtons.forEach((btn) => {
                btn.classList.remove('loading');
                btn.removeAttribute('aria-disabled');
                const spinner = btn.querySelector('.loading-overlay__spinner');
                if (spinner) spinner.classList.add('hidden');
              });
              if (this.submitButton && this.submitButton.dataset.soldOut === 'true') this.submitButton.setAttribute('aria-disabled', true);
            });
        }

        handleErrorMessage(errorMessage = false) {
          if (this.hideErrors) return;
          this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
          if (!this.errorMessageWrapper) return;
          this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
          this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
          if (errorMessage) this.errorMessage.textContent = errorMessage;
        }

        toggleSubmitButton(disable = true, text) {
          if (!this.submitButton) return;
          if (disable) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('aria-disabled', 'true');
            if (text && this.submitButtonText) this.submitButtonText.textContent = text;
          } else {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ variant-selects */
  if (!customElements.get('variant-selects')) {
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onVariantChange.bind(this));
        this.skipNonExistent = this.dataset.skipNonExistent === 'true';
        this.skipUnavailable = this.dataset.skipUnavailable === 'true';
      }

      get sectionId() {
        return this.dataset.section;
      }

      onVariantChange(event) {
        this.updateOptions();
        this.updateMasterId();
        this.updateLabels();
        this.toggleAddButton(true, '', false);
        this.updateVariantStatuses();
        if (!this.currentVariant) {
          if (this.skipNonExistent && this.findExistingVariant()) return;
          this.toggleAddButton(true, '', true);
          this.setUnavailable();
          return;
        }
        this.updateMedia();
        this.updateURL();
        this.updateVariantInput();
        this.updateStickySelect();
        this.renderProductInfo();
        this.updateShareUrl();
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html: null, variant: this.currentVariant },
        });
      }

      updateOptions() {
        this.options = Array.from(this.querySelectorAll('.product-form__input')).map((input) => {
          const checked = input.querySelector('input[type="radio"]:checked');
          if (checked) return checked.value;
          const select = input.querySelector('select');
          return select ? select.value : null;
        });
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
          return !variant.options.map((option, index) => this.options[index] === option).includes(false);
        });
      }

      findExistingVariant() {
        // jump to the first variant that matches the first option (Shrine data-skip-non-existent)
        const candidate = this.getVariantData().find((v) => v.options[0] === this.options[0]);
        if (!candidate) return false;
        this.setOptions(candidate.options);
        this.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      setOptions(values) {
        Array.from(this.querySelectorAll('.product-form__input')).forEach((input, index) => {
          const radio = input.querySelector(`input[type="radio"][value="${CSS.escape(values[index])}"]`);
          if (radio) {
            radio.checked = true;
            return;
          }
          const select = input.querySelector('select');
          if (select) select.value = values[index];
        });
      }

      updateLabels() {
        this.querySelectorAll('.product-form__input').forEach((input, index) => {
          const label = input.querySelector(`#custom-label-${this.sectionId}-${index + 1}`);
          if (!label) return;
          const name = label.dataset.optionName || label.textContent.split(' - ')[0].trim();
          label.dataset.optionName = name;
          label.textContent = `${name} - ${this.options[index]}`;
        });
      }

      updateMedia() {
        if (!this.currentVariant || !this.currentVariant.featured_media) return;
        const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.sectionId}"]`);
        mediaGalleries.forEach((mediaGallery) => mediaGallery.setActiveMedia(`${this.sectionId}-${this.currentVariant.featured_media.id}`, true));
        const modalContent = document.querySelector(`#ProductModal-${this.sectionId} .product-media-modal__content`);
        if (modalContent) {
          const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
          if (newMediaModal) modalContent.prepend(newMediaModal);
        }
        const stickyImage = document.querySelector(`#sticky-atc-image-${this.sectionId}`);
        if (stickyImage && this.currentVariant.featured_image) {
          stickyImage.src = this.currentVariant.featured_image.src + (this.currentVariant.featured_image.src.includes('?') ? '&' : '?') + 'width=500';
        }
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateShareUrl() {
        const shareButton = document.getElementById(`Share-${this.sectionId}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateVariantInput() {
        const productForms = document.querySelectorAll(`#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}`);
        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateStickySelect() {
        const stickySelect = document.querySelector(`#StickyAtcVariantPicker-${this.sectionId} select`);
        if (!stickySelect) return;
        const value = this.currentVariant.options.join(',');
        if (stickySelect.value !== value) stickySelect.value = value;
      }

      updateVariantStatuses() {
        const selectedOptionOneVariants = this.getVariantData().filter((variant) => this.options[0] === variant.option1);
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        inputWrappers.forEach((option, index) => {
          if (index === 0) return;
          const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
          const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked, option:checked');
          const previousValue = previousOptionSelected ? previousOptionSelected.value : this.options[index - 1];
          const availableOptionInputsValue = selectedOptionOneVariants
            .filter((variant) => variant.available && variant[`option${index}`] === previousValue)
            .map((variantOption) => variantOption[`option${index + 1}`]);
          this.setInputAvailability(optionInputs, availableOptionInputsValue);
        });
      }

      setInputAvailability(listOfOptions, listOfAvailableOptions) {
        listOfOptions.forEach((input) => {
          if (listOfAvailableOptions.includes(input.getAttribute('value'))) {
            if (input.tagName === 'OPTION') input.innerText = input.getAttribute('value');
            input.classList.remove('disabled');
            const swatch = input.closest('.color-swatch');
            if (swatch) swatch.classList.remove('disabled');
          } else {
            if (input.tagName === 'OPTION') {
              input.innerText = window.variantStrings.unavailable_with_option.replace('[value]', input.getAttribute('value'));
            }
            input.classList.add('disabled');
            const swatch = input.closest('.color-swatch');
            if (swatch) swatch.classList.add('disabled');
          }
        });
      }

      renderProductInfo() {
        const requestedVariantId = this.currentVariant.id;
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.sectionId;
        fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            if (this.currentVariant.id !== requestedVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const swap = (selector) => {
              const destination = document.querySelector(selector);
              const source = html.querySelector(selector);
              if (source && destination) destination.innerHTML = source.innerHTML;
            };
            swap(`#price-${this.sectionId}`);
            swap(`#sticky-atc-separate-price-${this.sectionId}`);
            const price = document.getElementById(`price-${this.sectionId}`);
            if (price) price.classList.remove('hidden');
            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
            this.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              window.variantStrings.soldOut
            );
          });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.sectionId}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] .main-atc__label__text');
        const stickyButton = document.getElementById(`SectionAtcBtn-${this.sectionId}`);
        const stickyText = stickyButton ? stickyButton.querySelector('.sticky-atc__label__text') : null;
        if (!addButton) return;
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          addButton.setAttribute('aria-disabled', 'true');
          if (stickyButton) stickyButton.setAttribute('aria-disabled', 'true');
          if (text) {
            if (addButtonText) addButtonText.textContent = text;
            if (stickyText) stickyText.textContent = text;
          }
        } else {
          addButton.removeAttribute('disabled');
          addButton.removeAttribute('aria-disabled');
          if (stickyButton) stickyButton.removeAttribute('aria-disabled');
          if (addButtonText) addButtonText.textContent = window.variantStrings.addToCart;
          if (stickyText) stickyText.textContent = window.variantStrings.addToCart;
        }
        if (!modifyClass) return;
      }

      setUnavailable() {
        const button = document.getElementById(`product-form-${this.sectionId}`);
        const addButton = button ? button.querySelector('[name="add"]') : null;
        const addButtonText = button ? button.querySelector('[name="add"] .main-atc__label__text') : null;
        const price = document.getElementById(`price-${this.sectionId}`);
        if (!addButton) return;
        if (addButtonText) addButtonText.textContent = window.variantStrings.unavailable;
        if (price) price.classList.add('hidden');
      }

      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }
    }
    customElements.define('variant-selects', VariantSelects);
  }

  /* ------------------------------------------------------------------ secondary-variant-select (sticky bar) */
  if (!customElements.get('secondary-variant-select')) {
    customElements.define(
      'secondary-variant-select',
      class extends HTMLElement {
        constructor() {
          super();
          this.select = this.querySelector('select');
          if (!this.select) return;
          this.select.addEventListener('change', this.onChange.bind(this));
        }
        onChange() {
          const values = this.select.value.split(',');
          const variantSelects = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
          if (variantSelects) {
            variantSelects.setOptions(values);
            variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
          // single-variant product: nothing to sync
        }
      }
    );
  }

  /* ------------------------------------------------------------------ product-info-upsell */
  if (!customElements.get('product-info-upsell')) {
    class ProductInfoUpsell extends HTMLElement {
      constructor() {
        super();
        this.image = this.querySelector('.upsell__image__img');
        this.toggleBtn = this.querySelector('.upsell-toggle-btn');
        this.skipNonExistent = this.dataset.skipNonExistent === 'true';
        this.skipUnavailable = this.dataset.skipUnavailable === 'true';
        if (this.toggleBtn) this.toggleBtn.addEventListener('click', this.handleToggle.bind(this));
      }
      handleToggle(event) {
        const tag = event.target.nodeName.toLowerCase();
        if (tag === 'select' || tag === 'option') return;
        event.preventDefault();
        this.dataset.selected = this.dataset.selected === 'true' ? 'false' : 'true';
      }
      updateId(id) {
        this.dataset.id = id;
      }
    }
    customElements.define('product-info-upsell', ProductInfoUpsell);
    window.ProductInfoUpsell = ProductInfoUpsell;
  }

  /* ------------------------------------------------------------------ dynamic-dates */
  if (!customElements.get('dynamic-dates')) {
    customElements.define(
      'dynamic-dates',
      class extends HTMLElement {
        constructor() {
          super();
          this.dateFormat = this.dataset.dateFormat || 'mm_dd';
          this.dayLabels = (this.dataset.dayLabels || 'Mon, Tue, Wed, Thu, Fri, Sat, Sun').split(',').map((s) => s.trim());
          this.monthLabels = (this.dataset.monthLabels || 'Jan, Feb, Mar, Apr, May, Jun, Jul, Aug, Sep, Oct, Nov, Dec').split(',').map((s) => s.trim());
          this.render();
          document.addEventListener('shopify:section:load', () => this.render());
        }
        ordinal(day) {
          if (day > 3 && day < 21) return 'th';
          switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
          }
        }
        format(date) {
          const day = date.getDate();
          const month = this.monthLabels[date.getMonth()];
          const weekday = this.dayLabels[(date.getDay() + 6) % 7];
          switch (this.dateFormat) {
            case 'dd_mm': return `${day}${this.ordinal(day)} ${month}`;
            case 'day_mm_dd': return `${weekday}, ${month} ${day}${this.ordinal(day)}`;
            case 'day_dd_mm': return `${weekday}, ${day}${this.ordinal(day)} ${month}`;
            default: return `${month} ${day}${this.ordinal(day)}`;
          }
        }
        render() {
          const today = new Date();
          this.querySelectorAll('[data-dynamic-date="true"]').forEach((el) => {
            const min = parseInt(el.dataset.minDays) || 0;
            const max = parseInt(el.dataset.maxDays) || 0;
            const start = new Date(today);
            start.setDate(today.getDate() + min);
            const end = new Date(today);
            end.setDate(today.getDate() + max);
            el.innerHTML = (el.dataset.text || '').replace('[start_date]', this.format(start)).replace('[end_date]', this.format(end));
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ sticky-atc */
  if (!customElements.get('sticky-atc')) {
    class StickyAtc extends HTMLElement {
      constructor() {
        super();
        this.isAfterScroll = this.dataset.afterScroll === 'true';
        this.mainAtcBtn = document.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        this.floatingBtns = document.querySelectorAll('.floating-btn');
        this.footerSpacing();
        if (this.isAfterScroll) {
          if (this.mainAtcBtn) {
            this.checkATCScroll();
            document.addEventListener('scroll', this.checkATCScroll.bind(this));
          }
        } else {
          this.style.transform = 'none';
          this.floatingBtns.forEach((btn) => btn.style.setProperty('--sticky-atc-offset', `${this.offsetHeight}px`));
        }
        // bridge: the bar's button submits the main product form
        this.button = this.querySelector('[id^="SectionAtcBtn-"]');
        if (this.button) {
          this.button.addEventListener('click', (event) => {
            const productForm = document.querySelector(`product-form[data-main="true"][data-section="${this.dataset.section}"]`) || document.querySelector('product-form[data-main="true"]');
            if (productForm && typeof productForm.handleSubmit === 'function') {
              event.preventDefault();
              productForm.handleSubmit(event);
            }
          });
        }
      }
      checkATCScroll() {
        if (window.scrollY > this.mainAtcBtn.offsetTop + this.mainAtcBtn.offsetHeight) {
          this.style.transform = 'none';
          this.scrolledPast = true;
        } else {
          this.style.transform = '';
          this.scrolledPast = false;
        }
        this.floatingBtns.forEach((btn) => btn.style.setProperty('--sticky-atc-offset', this.scrolledPast ? `${this.offsetHeight}px` : '0px'));
      }
      footerSpacing() {
        let spacer = document.querySelector('.sticky-atc-footer-spacer');
        if (spacer) return;
        spacer = document.createElement('div');
        spacer.className = 'sticky-atc-footer-spacer';
        spacer.style.height = `${this.clientHeight - 1}px`;
        spacer.style.display = 'block';
        const footer = document.querySelector('.footer');
        if (footer) spacer.style.background = window.getComputedStyle(footer).background;
        document.body.appendChild(spacer);
      }
    }
    customElements.define('sticky-atc', StickyAtc);
  }

  /* ------------------------------------------------------------------ product-info (container) */
  if (!customElements.get('product-info')) {
    customElements.define('product-info', class extends HTMLElement {});
  }
})();
